const React = require("react");
const Link = require("../../core/view/Link");
const URLMake = require("../../../shared/URLMake");
const {screenshot} = require("../../core/util/CopyShared");

const moment = require("moment");

const getAcademicYears = function() {
	const currentYear = moment().year();
	const currentMonth = moment().month();

	if (currentMonth < 5) {
		return [currentYear - 1, currentYear];
	} else {
		return [currentYear, currentYear + 1];
	}
};

const getCopy = function(styles, isSingleSchool) {
	return [
		{
			hash: "overview",
			titleLevel: 1,
			contents: (
				<div className={styles.rowContainer}>
					<div className={styles.column6}>
						<div className={styles.overviewText}>
							<p>
								{isSingleSchool ? "Schools" : "Districts"} have the option to
								upload their class rosters through CSV (Comma Separated Value)
								file import. Utilizing CSVs allows you to quickly input Class,
								Staff, and Student data. We recommend first preparing the file
								as a spreadsheet and then exporting it as a CSV.
							</p>
							<p>
								This option allows you to upload two CSV files with all the
								information about your classes, students, and staff users
								directly and securely to our servers. The rostering process
								requires <b>two files</b> for roster importing, a{" "}
								<Link
									to={URLMake.forSchoolSupport("rostering", "student-data-csv")}
									className={styles.link}
								>
									Student Data CSV
								</Link>{" "}
								and a{" "}
								<Link
									to={URLMake.forSchoolSupport("rostering", "staff-data-csv")}
									className={styles.link}
								>
									Staff Data CSV
								</Link>
								. These <b>two files must always be uploaded as a pair.</b> You
								may not upload one without the other. See below for details on
								how to prepare these two files.
							</p>
							<p>
								For smaller classes or simple edits, manual entry is available
								on the{" "}
								<Link
									to={URLMake.forSchoolSupport("accounts")}
									className={styles.link}
								>
									Accounts
								</Link>{" "}
								page.
							</p>
							<p>
								<b>To get started, </b>download your <b>current roster</b> or a{" "}
								<b>blank template</b> from your{" "}
								<Link
									to={URLMake.forSchool("accounts", "subscription")}
									className={styles.link}
								>
									Subscriptions
								</Link>{" "}
								page. See{" "}
								<Link
									to={URLMake.forSchoolSupport("rostering", "faq")}
									className={styles.link}
								>
									here
								</Link>{" "}
								for general recommendations on how to plan your rosters.
							</p>
						</div>
					</div>
					<div className={styles.column4}>
						<div className={styles.tableOfContents}>
							<div className={styles.tocHeader}>CONTENTS</div>
							<ul className={styles.blueBulletList}>
								<li>
									<Link
										to={URLMake.forSchoolSupport(
											"rostering",
											"how-to-save-csv"
										)}
										className={styles.link}
									>
										How to save a CSV file
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport(
											"rostering",
											"student-data-csv"
										)}
										className={styles.link}
									>
										Student data CSV
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport("rostering", "staff-data-csv")}
										className={styles.link}
									>
										Staff data CSV
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport("rostering", "file-upload")}
										className={styles.link}
									>
										File upload
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport(
											"rostering",
											"files-processed-what-now"
										)}
										className={styles.link}
									>
										The files are processed, what now?
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport("rostering", "faq")}
										className={styles.link}
									>
										Rostering FAQ
									</Link>
								</li>
								<li>
									<Link
										to={URLMake.forSchoolSupport(
											"rostering",
											"add-licenses-to-school"
										)}
										className={styles.link}
									>
										Adding student licenses to your school account
									</Link>
								</li>
							</ul>
						</div>
					</div>
				</div>
			),
		},
		{
			hash: "how-to-save-csv",
			title: "HOW TO SAVE A CSV FILE",
			titleLevel: 1,
			contents: (
				<div>
					<p>
						Many programs can edit a CSV file, including popular spreadsheet
						programs like Microsoft Excel or Google Sheets. Whichever program
						you use, you should have an option to save as <b>(*.csv).</b> If you
						have multiple CSV options, look for the plain{" "}
						<b>CSV (Comma Delimited) (*.csv)</b> option.
					</p>
				</div>
			),
		},
		{
			hash: "student-data-csv",
			title: "Student Data CSV",
			titleLevel: 1,
			contents: (
				<div>
					<p>
						The Student Data CSV must include all students who need access to
						Beast Academy Online. A new CSV will overwrite previously existing
						data. Students not included in the new CSV for the <i>current</i>{" "}
						<Link
							to={URLMake.forSchoolSupport("rostering", "faq")}
							className={styles.link}
						>
							academic year
						</Link>{" "}
						will be deactivated and lose access to Beast Academy Online.
						However, you can upload a CSV for the upcoming academic year without
						affecting student enrollment for the current year.
					</p>
					<p>
						If the number of students listed in the CSV exceeds your school
						subscription's student limit, the CSV will not be processed.
					</p>
					<p>
						<b>Note:</b> Each Class Name
						{isSingleSchool ? " " : " and School Name "}
						included in the Student Data CSV must also be assigned to{" "}
						<i>at least</i> one teacher in the Staff Data CSV.
					</p>
					<h3>File Name Structure</h3>
					<p>
						You can choose to name your Student Data CSV file however you like.
						Please note when you download the current roster, the file name will
						be structured as <i>BA-Academic year-students</i>. <b>Example:</b>{" "}
						ba-{getAcademicYears().join("-")}-students.csv.
					</p>
					<ul>
						<li>Column A: Student ID</li>
						<li>Column B: First Name</li>
						<li>Column C: Last Name</li>
						<li>Column D: Password</li>
						<li>Column E: Class Name</li>
						{isSingleSchool ? null : <li>Column F: School Name</li>}
					</ul>
					<p>
						<b>Note</b> that{" "}
						<Link
							to={URLMake.forSchoolSupport("rostering", "faq")}
							className={styles.link}
						>
							student usernames
						</Link>{" "}
						are automatically assigned.
					</p>
					<p>
						Following the header, every row should correspond to a single
						student. There should be {isSingleSchool ? "five" : "six"} values in
						each row, matching the order of the column headers above.
					</p>
					<table className={styles.guideTable}>
						<thead>
							<tr>
								<th>Header Row</th>
								<th>Description of Contents</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									<b>Student ID</b>
								</td>
								<td>
									This is a school-assigned ID number for the student.
									<ul>
										<li>
											<b>Cannot be changed once a student is created.</b>
										</li>
										<li>
											<b>Must use unique values for each student.</b>
										</li>
										<li>It can be an alphanumeric value.</li>
										<li>
											Students are identified in our system using this ID.
										</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>
									<b>First Name</b>
								</td>
								<td>The first name of the student.</td>
							</tr>
							<tr>
								<td>
									<b>Last Name</b>
								</td>
								<td>The last name of the student.</td>
							</tr>
							<tr>
								<td>
									<b>Password</b>
								</td>
								<td>
									The password that the student will sign in with.
									<ul>
										<li>
											<b>Passwords must be at least 5 characters,</b> and may
											not include identifiable information such as their first
											or last name.
										</li>
										<li>
											We recommend coming up with a format that makes it easy to
											tell students their password without having to write it
											out for them.
											<p>
												<b>Example:</b> the first 2 letters of the student's
												first name + their Student ID
											</p>
										</li>
										<li>
											<b>Leave this field blank</b> if the student already
											exists in your Beast Academy School account <b>unless</b>{" "}
											you need to change the password.
										</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>
									<b>Class Name</b>
								</td>
								<td>
									The name of the class you are placing the student in.
									<ul>
										<li>Students can only be assigned to one class.</li>
										<li>
											Each class in a school must have a unique class name.
										</li>
									</ul>
								</td>
							</tr>
							{isSingleSchool ? null : (
								<tr>
									<td>
										<b>School Name</b>
									</td>
									<td>
										The name of the school that the student is in.
										<ul>
											<li>
												Each school in your Beast Academy Schools account must
												have a unique school name.
											</li>
										</ul>
									</td>
								</tr>
							)}
						</tbody>
					</table>
					<p>
						<b>Example Student spreadsheet:</b>
					</p>
					<table className={styles.guideTable}>
						<thead>
							<tr>
								<th>Student ID</th>
								<th>First Name</th>
								<th>Last Name</th>
								<th>Password</th>
								<th>Class Name</th>
								{isSingleSchool ? null : <th>School Name</th>}
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>20001</td>
								<td>Jamie</td>
								<td>Jameson</td>
								<td>ja2399</td>
								<td>4th Grade (Smith)</td>
								{isSingleSchool ? null : <td>Jules Verne Elementary</td>}
							</tr>
							<tr>
								<td>20002</td>
								<td>Amber</td>
								<td>Lee</td>
								<td>am4210</td>
								<td>5th Grade (Rosen)</td>
								{isSingleSchool ? null : <td>Jules Verne Elementary</td>}
							</tr>
							<tr>
								<td>20003</td>
								<td>Steward</td>
								<td>West</td>
								<td>st8298</td>
								<td>3rd Grade (Montoya)</td>
								{isSingleSchool ? null : <td>George Washington Elementary</td>}
							</tr>
						</tbody>
					</table>
				</div>
			),
		},
		{
			hash: "staff-data-csv",
			title: "Staff Data CSV",
			titleLevel: 1,
			contents: (
				<div>
					<p>
						The Staff Data CSV should include all staff members who need access
						to Beast Academy Online. A new CSV will overwrite previously
						existing data. Staff members not included in a new CSV for the
						active{" "}
						<Link
							to={URLMake.forSchoolSupport("rostering", "faq")}
							className={styles.link}
						>
							academic year
						</Link>{" "}
						will be deactivated and will lose access to Beast Academy Online.
					</p>
					<p>
						If the number of staff members listed exceed your school
						subscription's teacher limit, the CSV will not be processed.
					</p>
					<h3>What are the different types of staff members?</h3>
					{isSingleSchool ? (
						<p>
							<b>School Administrator:</b> This user can control
							adding/deleting/editing staff, students, and rostering within the
							school.
						</p>
					) : (
						<React.Fragment>
							<p>
								<b>System Administrator:</b> This user controls
								adding/deleting/editing schools, staff, and students for the
								entire system. This user has the ability to upload the CSVs as
								part of the Rostering process.
							</p>
							<p>
								<b>School Administrator:</b> This user can manually
								add/delete/edit classes, staff, and students within their
								school. This user does not have the ability to upload the CSVs
								as part of the Rostering process.
							</p>
						</React.Fragment>
					)}
					<p>
						<b>Teacher:</b> This user has the ability to manually change the
						Grade, BA level, and Lock Scheme for their class. Additionally, this
						user has the ability to manually edit the display name or password
						for students within their class.
					</p>
					<p>
						This user <b>cannot</b> use the Rostering process and{" "}
						<b>does not have the ability</b> to activate, deactivate, or
						transfer students manually.
					</p>
					<h3>File Name Structure</h3>
					<p>
						You can choose to name your Staff Data CSV file however you like.
						Please note when you download the current roster, the file name will
						be structured as <i>BA-Academic year-staff</i>. <b>Example:</b> ba-
						{getAcademicYears().join("-")}-staff.csv.
					</p>
					<p>
						The Staff Data CSV template you download includes these headers.
					</p>
					<ul>
						<li>Column A: Staff Email</li>
						<li>Column B: First Name</li>
						<li>Column C: Last Name</li>
						<li>Column D: Class Name</li>
						{isSingleSchool ? (
							<React.Fragment>
								<li>Column E: Grade</li>
								<li>Column F: BA Level</li>
								<li>Column G: School Admin</li>
							</React.Fragment>
						) : (
							<React.Fragment>
								<li>Column E: School Name</li>
								<li>Column F: Grade</li>
								<li>Column G: BA Level</li>
								<li>Column H: School Admin</li>
								<li>Column I: System Admin</li>
							</React.Fragment>
						)}
					</ul>
					<p>
						Following the headers, each row should correspond to a single staff
						member. Staff members teaching multiple classes will need{" "}
						<b>a row for each class.</b>{" "}
						{isSingleSchool ? (
							"There should be seven"
						) : (
							<React.Fragment>
								Administrative staff assigned to multiple schools need a{" "}
								<b>row for each school.</b> There should be nine
							</React.Fragment>
						)}{" "}
						values in each row, matching the order of the columns headers above.
					</p>
					<table className={styles.guideTable}>
						<thead>
							<tr>
								<th>Header Row</th>
								<th>Optional?</th>
								<th>Description of Contents</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									<b>Staff Email</b>
								</td>
								<td>No</td>
								<td>This is the unique email address for the staff member.</td>
							</tr>
							<tr>
								<td>
									<b>First Name</b>
								</td>
								<td>No</td>
								<td>This is the first name of the staff member.</td>
							</tr>
							<tr>
								<td>
									<b>Last Name</b>
								</td>
								<td>No</td>
								<td>This is the last name of the staff member.</td>
							</tr>
							<tr>
								<td>
									<b>Class Name</b>
								</td>
								<td>See description</td>
								<td>
									This is the name of the class the staff member teaches.
									<ul>
										<li>
											Every class at a school must have a unique class name.
										</li>
										<li>
											If a staff member teaches <b>multiple classes,</b> you
											must include a <b>separate row</b> for each class that
											they teach.
										</li>
										<li>
											If the staff member is a school admin, this can be left
											blank.
										</li>
									</ul>
								</td>
							</tr>
							{isSingleSchool ? null : (
								<tr>
									<td>
										<b>School Name</b>
									</td>
									<td>See description</td>
									<td>
										This is the name of the school that the class is in.
										<ul>
											<li>
												Every school in your Beast Academy Schools account must
												have a unique school name.
											</li>
											<li>
												This cannot be left blank if the staff member is a
												school admin (i.e. if there is a "Y" under the School
												Admin column header).
											</li>
											<li>
												If a staff member is a School Admin at multiple schools,
												you must include a separate row for each school.
											</li>
											<li>
												If the staff member is a system admin, this can be left
												blank.
											</li>
										</ul>
									</td>
								</tr>
							)}
							<tr>
								<td>
									<b>Grade</b>
								</td>
								<td>See description</td>
								<td>
									This is the grade level for the class.
									<ul>
										<li>
											Use 0 for Kindergarten, or a number from 1-8 for grades
											1-8.
										</li>
										<li>
											This can be left blank if there is no Class Name set.
										</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>
									<b>BA Level</b>
								</td>
								<td>Yes</td>
								<td>
									This is the Beast Academy level the entire class will start
									at.
									<ul>
										<li>
											Your CSV should include a number from 1-5 in this field.
										</li>
										<li>
											We recommend choosing a BA level that is one below the
											class' grade.
										</li>
										<li>
											This can be left blank if there is no Class Name set.
										</li>
									</ul>
								</td>
							</tr>
							<tr>
								<td>
									<b>School Admin</b>
								</td>
								<td>Yes</td>
								<td>
									Write "Y" if the staff member is an administrator of the
									school. Otherwise, leave blank.
								</td>
							</tr>
							{isSingleSchool ? null : (
								<tr>
									<td>
										<b>System Admin</b>
									</td>
									<td>Yes</td>
									<td>
										Write "Y" if the staff member is a system administrator of
										the Beast Academy Schools account. Otherwise, leave blank.
									</td>
								</tr>
							)}
						</tbody>
					</table>
					<p>
						<b>Example Staff spreadsheet:</b>
					</p>
					<table className={styles.guideTable}>
						<thead>
							<tr>
								<th>Staff Email</th>
								<th>First Name</th>
								<th>Last Name</th>
								<th>Class Name</th>
								{isSingleSchool ? null : <th>School Name</th>}
								<th>Grade</th>
								<th>BA Level</th>
								<th>School Admin</th>
								{isSingleSchool ? null : <th>System Admin</th>}
							</tr>
						</thead>
						{isSingleSchool ? (
							<tbody>
								<tr>
									<td>smith@mont.edu</td>
									<td>Ella</td>
									<td>Smith</td>
									<td>4th Grade (Smith)</td>
									<td>4</td>
									<td>3</td>
									<td />
								</tr>
								<tr>
									<td>rosen@mont.edu</td>
									<td>Maurice</td>
									<td>Rosen</td>
									<td>5th Grade (Rosen)</td>
									<td>5</td>
									<td />
									<td>Y</td>
								</tr>
								<tr>
									<td>montoya@mont.edu</td>
									<td>Wesley</td>
									<td>Montoya</td>
									<td>3rd Grade (Montoya)</td>
									<td>3</td>
									<td>3</td>
									<td />
								</tr>
								<tr>
									<td>montoya@mont.edu</td>
									<td>Wesley</td>
									<td>Montoya</td>
									<td>2nd Grade (Montoya)</td>
									<td>2</td>
									<td />
									<td />
								</tr>
								<tr>
									<td>kstein@mont.edu</td>
									<td>Karen</td>
									<td>Stein</td>
									<td>2nd Grade (Montoya)</td>
									<td>2</td>
									<td>2</td>
									<td>Y</td>
								</tr>
							</tbody>
						) : (
							<tbody>
								<tr>
									<td>smith@mont.edu</td>
									<td>Ella</td>
									<td>Smith</td>
									<td>4th Grade (Smith)</td>
									<td>Jules Verne Elementary</td>
									<td>4</td>
									<td>3</td>
									<td />
									<td />
								</tr>
								<tr>
									<td>rosen@mont.edu</td>
									<td>Maurice</td>
									<td>Rosen</td>
									<td>5th Grade (Rosen)</td>
									<td>Jules Verne Elementary</td>
									<td>5</td>
									<td />
									<td>Y</td>
									<td />
								</tr>
								<tr>
									<td>montoya@mont.edu</td>
									<td>Wesley</td>
									<td>Montoya</td>
									<td>3rd Grade (Montoya)</td>
									<td>George Washington Elementary</td>
									<td>3</td>
									<td>3</td>
									<td />
									<td />
								</tr>
								<tr>
									<td>montoya@mont.edu</td>
									<td>Wesley</td>
									<td>Montoya</td>
									<td>2nd Grade (Montoya)</td>
									<td>George Washington Elementary</td>
									<td>2</td>
									<td />
									<td />
									<td />
								</tr>
								<tr>
									<td>chowdury@mont.edu</td>
									<td>Jessica</td>
									<td>Chowdury</td>
									<td />
									<td>George Washington Elementary</td>
									<td />
									<td />
									<td>Y</td>
									<td />
								</tr>
								<tr>
									<td>greer@mont.edu</td>
									<td>Gwen</td>
									<td>Greer</td>
									<td />
									<td />
									<td />
									<td />
									<td />
									<td>Y</td>
								</tr>
							</tbody>
						)}
					</table>
					<br />
					In the example above, there are {isSingleSchool ? "4" : "5"} staff
					members in your Beast Academy Schools account:
					{isSingleSchool ? (
						<ul>
							<li>Smith is a teacher for one class.</li>
							<li>
								Rosen is just a School Administrator and is not teaching a
								class.
							</li>
							<li>Montoya is a teacher for two classes.</li>
							<li>
								Stein is a school administrator but also teaches class “2nd
								Grade” with Montoya.
							</li>
						</ul>
					) : (
						<ul>
							<li>
								Smith and Rosen are both teachers at Jules Verne Elementary.
							</li>
							<li>
								Rosen is also a school administrator at Jules Verne Elementary.
							</li>
							<li>
								Montoya is a teacher for two classes at George Washington
								Elementary.
							</li>
							<li>
								Chowdhury is a school administrator for George Washington
								Elementary.
							</li>
							<li>Greer is a system administrator.</li>
						</ul>
					)}
					<h3>Deactivating A Class</h3>
					<p>
						To deactivate a class, make sure the class is removed from the staff
						and student CSVs before uploading your files. The class must not be
						included anywhere else in your CSV files. You will only need to do
						this once. In future downloads, any deactivated classes will not
						appear on your rosters.
					</p>
					<h3>Renaming A Class</h3>
					<p>
						You should rename classes manually in your{" "}
						<Link
							to={URLMake.forSchoolSupport("accounts")}
							className={styles.link}
						>
							Accounts
						</Link>{" "}
						page before downloading your rosters to make updates. Uploading CSV
						files with class names that do not currently match a class in your
						account will create a new class.
					</p>
				</div>
			),
		},
		{
			hash: "file-upload",
			title: "File Upload",
			titleLevel: 1,
			contents: (
				<div>
					<p>
						Once the Student Data CSV and Staff Data CSV are both prepared, you
						can securely upload them to our server using the file upload option
						located on the{" "}
						<Link
							to={URLMake.forSchoolRostering("validate")}
							className={styles.link}
						>
							Subscriptions
						</Link>{" "}
						page.
					</p>
					<p>
						If you run into any issues, please reach out to
						schools@inflectionpointlearning.com for assistance.
					</p>
				</div>
			),
		},
		{
			hash: "files-processed-what-now",
			title: "The files are processed, what now?",
			titleLevel: 1,
			contents: (
				<div>
					<p>
						If you are not given an error message during the upload process, you
						should be all set. You can confirm your changes by checking the{" "}
						{isSingleSchool ? null : (
							<React.Fragment>
								<Link
									to={URLMake.forSchool("accounts", "schools")}
									className={styles.link}
								>
									Schools
								</Link>
								,{" "}
							</React.Fragment>
						)}
						<Link
							to={URLMake.forSchool("accounts", "classes")}
							className={styles.link}
						>
							Classes
						</Link>
						,{" "}
						<Link
							to={URLMake.forSchool("accounts", "staff")}
							className={styles.link}
						>
							Staff
						</Link>
						, and{" "}
						<Link
							to={URLMake.forSchool("accounts", "students")}
							className={styles.link}
						>
							Students
						</Link>{" "}
						tabs on your{" "}
						<Link
							to={URLMake.forSchool("accounts", "classes")}
							className={styles.link}
						>
							Account
						</Link>{" "}
						page.
					</p>
				</div>
			),
		},
		{
			hash: "faq",
			title: "Rostering FAQ",
			titleLevel: 1,
			contents: (
				<div>
					<ul>
						<li>
							<p>
								<b>
									How should I plan my Roster? / How should classes be set up?
								</b>
								<br />
								We recommend that students are assigned to classes by BA Level.
								The BA Level will determine, for the whole class, where students
								start the BA program. In certain use cases, some schools may
								find they have students assigned to different teachers than
								those teaching a particular BA Level, which can be solved by
								assigning multiple teachers to a class.
							</p>
						</li>
					</ul>
					<ul>
						<li>
							<p>
								<b>
									Where's my student's username? / Can I change a student's
									username?
								</b>
								<br />
								Usernames are automatically assigned when a student is added to
								the Beast Academy system. Usernames cannot be changed or
								manually selected. Once your CSVs are processed, you can see the
								assigned student usernames in your{" "}
								<Link
									to={URLMake.forSchool("accounts", "students")}
									className={styles.link}
								>
									Students
								</Link>{" "}
								page.
							</p>
							<p>
								Here is an example of what a typical username will look like:
								ThoughtfulPorcupine68.
							</p>
						</li>
					</ul>
					<ul>
						<li>
							<p>
								<b>What's next for new staff members?</b>
								<br />
								Once staff members have been created within your Beast Academy
								Schools account, they will receive an email from our system
								asking them to verify their email address. You should let your
								staff know to expect this email and to check their spam folders.
							</p>
							<p>
								If needed, you can resend this email from the{" "}
								<Link
									to={URLMake.forSchool("accounts", "staff")}
									className={styles.link}
								>
									Staff
								</Link>{" "}
								page. Select the staff member's name and on the following
								screen, click on the Resend email link in the upper right
								corner.
							</p>
							{screenshot(styles, "accountResendSetupEmail", false)}
						</li>
					</ul>
					<ul>
						<li>
							<p>
								<b>
									How do I make edits? / Do I need to download the CSV every
									time?
								</b>
								<br />
								For <b>quick edits,</b> you can make manual changes in your
								Accounts page. For example, you may want to manually add one
								student or change a student's password.
							</p>
							<p>
								Anytime you want to make <b>multiple changes </b>at once, you'll
								have to upload CSVs. Download your Staff and Student Data CSVs
								every time you do this to make sure you have the most up-to-date
								information.
							</p>
						</li>
					</ul>
					<ul>
						<li>
							<p>
								<b>What is the Academic Year End Date?</b>
								<br />
								This is the date in which all your new classes for the new
								school year are activated. If you have not uploaded new rosters
								prior to this date your previous academic year rosters will roll
								over into the new school year. You can find your Academic Year
								End Date listed on your{" "}
								<Link
									to={URLMake.forSchool("accounts", "subscription")}
									className={styles.link}
								>
									Subscriptions
								</Link>{" "}
								page.
							</p>
						</li>
					</ul>
				</div>
			),
		},
		{
			hash: "add-licenses-to-school",
			title: "Adding student licenses to your school account",
			titleLevel: 1,
			contents: (
				<div>
					<h3>Instructions for adding seats:</h3>
					<p>
						Please note that student licenses can only be added in minimums of
						5.{" "}
					</p>
					<ol>
						<li>
							Make sure you are logged in to your{" "}
							<a
								href="https://beastacademy.com/signin"
								target="_blank"
								className={styles.link}
							>
								Administrator account
							</a>
							.
						</li>
						<li>
							Go to the Accounts {">"} Subscriptions{" "}
							<a
								href="https://beastacademy.com/dashboard/accounts/system/subscription "
								target="_blank"
								className={styles.link}
							>
								page
							</a>
							.
						</li>
						<li>
							<p>
								Click the "Increase Limit" button next to the "Student Limit" as
								shown in the screenshot below:{" "}
							</p>
							{screenshot(styles, "increase-student-licenses", false)}
						</li>
						<li>
							<p>
								Enter the amount of seats you wish to add (Minimum of 5, Maximum
								of 1000) and see the cost per seat.{" "}
							</p>
							{screenshot(styles, "purchase-student-licenses", false)}
						</li>
						<li>
							{" "}
							Once you have finished entering the number of seats you wish to
							add, you will see a checkbox for agreeing to the terms.
						</li>
						<li>
							You will receive a confirmation modal as well as an email with a
							summary of your seat addition information on how to receive and
							pay your invoice.{" "}
						</li>
					</ol>
					<h3>Adding Staff Accounts</h3>
					<ol start="7">
						<li>
							Please note that for every 5 students added 1 additional staff
							account will be automatically added to your school's subscription
							free of charge.
						</li>
					</ol>
				</div>
			),
		},
	];
};

module.exports = {
	getCopy,
};
